// src/pages/GradingPage.js
//
// REESCRITO — ya no usa gradingStore.js (mock, rúbricas hardcodeadas).
// Ahora usa entregaService y rubricaService reales.
//
// DIFERENCIAS DE MODELO frente al mock (huecos reales del backend,
// no simplificaciones arbitrarias):
//   1. Una rúbrica real está atada a UN contenido específico
//      (rubricaService.getByContenido), no es una lista de rúbricas
//      genéricas para elegir — si el contenido no tiene rúbrica
//      definida, solo queda disponible la calificación rápida.
//   2. Los criterios reales no tienen "niveles" con descripción (el
//      mock sí, ej. "Excelente/Bueno/Aceptable") — solo nombre,
//      descripción, peso% y puntaje máximo. El slider queda 0..puntosMax
//      sin marks nombrados.
//   3. "Devolver para corrección" NO EXISTE en el backend — el modelo
//      de datos solo tiene Enviada → Calificada, sin un estado de
//      devolución. Se quitó el botón/dialog en vez de simular algo
//      que no pasa nada en el servidor.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button, Avatar, Chip,
  List, ListItem, ListItemAvatar, ListItemText, IconButton, Divider, Paper,
  Tab, Tabs, TextField, Slider, Alert, LinearProgress, Breadcrumbs, Link,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Grade as GradeIcon, CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon, AttachFile as AttachFileIcon, Download as DownloadIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { entregaService, rubricaService } from '../services/api';

function GradingPage() {
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const { user } = useAuth();

  const [pendientes, setPendientes] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const [rubrica, setRubrica] = useState(null); // null = sin rúbrica para este contenido
  const [gradingMode, setGradingMode] = useState('quick'); // 'rubric' solo si hay rúbrica
  const [criteriaScores, setCriteriaScores] = useState({});
  const [feedback, setFeedback] = useState('');
  const [quickScore, setQuickScore] = useState(80);
  const [enviando, setEnviando] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);

  // ── Cargar entregas pendientes (ya filtradas por instructor en el backend) ──
  const cargarPendientes = useCallback(async () => {
    setCargandoLista(true);
    try {
      const list = await entregaService.getPendientesInstructor();
      setPendientes(list);
      return list;
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar las entregas pendientes.');
      return [];
    } finally {
      setCargandoLista(false);
    }
  }, []);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  // Si llegamos con submissionId en la URL, seleccionarla al cargar la lista
  useEffect(() => {
    if (!submissionId || pendientes.length === 0) return;
    const found = pendientes.find((s) => s.id === submissionId);
    if (found) setSelectedSubmission(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, pendientes]);

  // ── Al seleccionar una entrega: traer detalle completo + su rúbrica ──
  useEffect(() => {
    if (!selectedSubmission) { setRubrica(null); return; }
    (async () => {
      setCargandoDetalle(true);
      setError(null);
      try {
        const [detalle, rub] = await Promise.all([
          entregaService.getById(selectedSubmission.id),
          rubricaService.getByContenido(selectedSubmission.contentId),
        ]);
        setSelectedSubmission(detalle);
        setRubrica(rub);
        if (rub) {
          const initialScores = {};
          rub.criteria.forEach((c) => { initialScores[c.id] = { score: 0, comment: '' }; });
          setCriteriaScores(initialScores);
          setGradingMode('rubric');
        } else {
          setGradingMode('quick');
        }
      } catch (e) {
        setError(e?.message || 'No se pudo cargar el detalle de la entrega.');
      } finally {
        setCargandoDetalle(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubmission?.id]);

  const calculateTotalScore = () => {
    if (!rubrica) return 0;
    const totalPosible = rubrica.criteria.reduce((sum, c) => sum + (c.maxPoints || 0), 0);
    if (totalPosible === 0) return 0;
    const totalObtenido = rubrica.criteria.reduce((sum, c) => sum + (criteriaScores[c.id]?.score || 0), 0);
    return Math.round((totalObtenido / totalPosible) * 100);
  };

  const handleCriteriaChange = (criteriaId, score) => {
    setCriteriaScores((prev) => ({ ...prev, [criteriaId]: { ...prev[criteriaId], score } }));
  };

  const handleCriteriaComment = (criteriaId, comment) => {
    setCriteriaScores((prev) => ({ ...prev, [criteriaId]: { ...prev[criteriaId], comment } }));
  };

  const handleSubmitGrade = async () => {
    if (!selectedSubmission) return;
    setEnviando(true);
    setError(null);
    try {
      if (gradingMode === 'rubric' && rubrica) {
        const score = calculateTotalScore();
        const rubricaPayload = rubrica.criteria.map((c) => ({
          criterionId: c.id, points: criteriaScores[c.id]?.score || 0, note: criteriaScores[c.id]?.comment || '',
        }));
        await entregaService.calificar(selectedSubmission.id, score, feedback, rubricaPayload);
      } else {
        await entregaService.calificar(selectedSubmission.id, quickScore, feedback, null);
      }
      setSuccessMessage('¡Calificación enviada exitosamente!');
      const list = await cargarPendientes();
      setTimeout(() => {
        setSuccessMessage('');
        setSelectedSubmission(null);
        setFeedback('');
      }, 1500);
    } catch (e) {
      setError(e?.message || 'No se pudo enviar la calificación.');
    } finally {
      setEnviando(false);
    }
  };

  const totalScore = calculateTotalScore();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/instructor'); }}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Dashboard
          </Link>
          <Typography color="text.primary">Calificaciones</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/instructor')}><ArrowBackIcon /></IconButton>
          <Typography variant="h4" fontWeight={700}>📝 Centro de Calificaciones</Typography>
        </Box>

        {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Entregas Pendientes ({pendientes.length})
                </Typography>

                {cargandoLista ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : pendientes.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography>¡Todo calificado! 🎉</Typography>
                  </Box>
                ) : (
                  <List>
                    {pendientes.map((submission, index) => (
                      <React.Fragment key={submission.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          button
                          selected={selectedSubmission?.id === submission.id}
                          onClick={() => setSelectedSubmission(submission)}
                          sx={{ borderRadius: 1, mb: 0.5, border: selectedSubmission?.id === submission.id ? '2px solid' : 'none', borderColor: 'primary.main' }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                              {submission.studentName?.[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={<Typography variant="body2" fontWeight={600}>{submission.studentName}</Typography>}
                            secondary={<Typography variant="caption" color="text.secondary" noWrap>{submission.contentTitle}</Typography>}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {!selectedSubmission ? (
              <Card sx={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <GradeIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">Selecciona una entrega para calificar</Typography>
                </Box>
              </Card>
            ) : cargandoDetalle ? (
              <Card sx={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Card>
            ) : (
              <Card sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
                <CardContent>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>{selectedSubmission.studentName?.[0]}</Avatar>
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h6" fontWeight={600}>{selectedSubmission.studentName}</Typography>
                        <Typography variant="body2" color="text.secondary">{selectedSubmission.contentTitle}</Typography>
                      </Grid>
                      <Grid item>
                        <Chip icon={<AccessTimeIcon />} label={new Date(selectedSubmission.submittedAt).toLocaleString()} size="small" variant="outlined" />
                      </Grid>
                    </Grid>

                    {selectedSubmission.studentComment && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>💬 Comentario del estudiante:</Typography>
                        <Typography variant="body2" color="text.secondary">{selectedSubmission.studentComment}</Typography>
                      </Box>
                    )}

                    {selectedSubmission.files?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>📎 Archivos entregados:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedSubmission.files.map((file) => (
                            <Chip key={file.id} icon={<AttachFileIcon />} label={file.fileName} variant="outlined" size="small"
                              component="a" href={file.url} target="_blank" clickable deleteIcon={<DownloadIcon />} onDelete={() => window.open(file.url, '_blank')} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>

                  {rubrica && (
                    <Tabs value={gradingMode === 'rubric' ? 0 : 1} onChange={(e, v) => setGradingMode(v === 0 ? 'rubric' : 'quick')} sx={{ mb: 3 }}>
                      <Tab label="📋 Calificar con Rúbrica" />
                      <Tab label="⚡ Calificación Rápida" />
                    </Tabs>
                  )}

                  {gradingMode === 'rubric' && rubrica ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>{rubrica.name}</Typography>
                      {rubrica.criteria.map((criterion) => (
                        <Paper key={criterion.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>{criterion.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{criterion.description}</Typography>
                            </Box>
                            <Chip label={`${criterion.weightPercent}%`} color="primary" size="small" />
                          </Box>

                          <Box sx={{ px: 2, mb: 1 }}>
                            <Slider
                              value={criteriaScores[criterion.id]?.score || 0}
                              onChange={(e, v) => handleCriteriaChange(criterion.id, v)}
                              step={1} min={0} max={criterion.maxPoints || 100}
                              valueLabelDisplay="on"
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {criteriaScores[criterion.id]?.score || 0} / {criterion.maxPoints} puntos
                          </Typography>

                          <TextField
                            fullWidth size="small" placeholder="Comentario para este criterio (opcional)"
                            value={criteriaScores[criterion.id]?.comment || ''}
                            onChange={(e) => handleCriteriaComment(criterion.id, e.target.value)}
                          />
                        </Paper>
                      ))}

                      <Paper sx={{ p: 2, mb: 3, bgcolor: totalScore >= 60 ? 'success.dark' : 'error.dark', color: 'white' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6">Puntaje Total</Typography>
                          <Typography variant="h3" fontWeight={700}>{totalScore}/100</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={totalScore} sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)' }} />
                      </Paper>
                    </>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                      <Typography variant="h6" gutterBottom>Puntaje Directo</Typography>
                      {!rubrica && (
                        <Alert severity="info" sx={{ mb: 2 }}>Este contenido no tiene rúbrica configurada — solo calificación directa.</Alert>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider value={quickScore} onChange={(e, v) => setQuickScore(v)} min={0} max={100} valueLabelDisplay="on" sx={{ flex: 1 }} />
                        <TextField
                          type="number" value={quickScore}
                          onChange={(e) => setQuickScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                          sx={{ width: 100 }} InputProps={{ endAdornment: <Typography>/100</Typography> }}
                        />
                      </Box>
                    </Paper>
                  )}

                  <TextField
                    fullWidth multiline rows={4} label="Retroalimentación para el estudiante"
                    placeholder="Escribe comentarios generales sobre el trabajo..."
                    value={feedback} onChange={(e) => setFeedback(e.target.value)} sx={{ mb: 3 }}
                  />

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="contained" color="primary" startIcon={<SendIcon />}
                      onClick={handleSubmitGrade} disabled={enviando}>
                      {enviando ? 'Enviando…' : 'Enviar Calificación'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default GradingPage;
